import {
  lazy,
  Suspense,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import { Box, Fab, Tooltip } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { agentConfig } from "@/shared/config/agentConfig";
import { describeVoiceState, isVoiceInputSupported } from "@/shared/hooks/useVoiceInput";
import { primeListenCue } from "../chat/listenCue";
import { getSpeakingEntryId, subscribeSpeech } from "../chat/speechPlayback";
import { useAgentTasks } from "../tasks/useAgentTasks";
import {
  voiceClusterMotionSx,
  voiceClusterPhaseColors,
  voiceClusterPhaseHover,
  voiceClusterReducedMotionSx,
  voiceClusterStatusShadow,
} from "./aiAssist/animations";
import { AGENT_DRAWER_WIDTH } from "./agentDrawerLayout";
import TowerAssistantFab, { TOWER_ASSISTANT_FAB_SIZE_PX } from "./TowerAssistantFab";
import TowerAssistantVoiceButton, {
  VOICE_CLOSE_BUTTON_PX,
  VOICE_LAUNCH_GAP_PX,
} from "./TowerAssistantVoiceButton";
import { useAiAssistCompactLayout } from "./useAiAssistCompactLayout";
import { useOptionalAgentSession } from "./AgentSessionProvider";
import {
  VOICE_MODE_STOP_TOOLTIP,
  VOICE_PHASE_LABEL,
  type VoiceLaunchAction,
  type VoiceLaunchActivity,
  type VoiceLaunchCommand,
} from "./voiceLaunch";
import {
  FAB_SCREEN_EDGE_PX,
  MOBILE_BOTTOM_NAV_HEIGHT_PX,
} from "@/shared/ui/appHeader/mobileBottomNav";
import { useHeaderCompact } from "@/shared/ui/useCompactLayout";
import {
  CHROME_FAB_POINTER_VAR,
  chromeFabBottom,
  chromeFabOpacity,
} from "@/shared/ui/compactChrome/chromeCss";
import { desktopFabBottom } from "@/shared/ui/tableFooterClearance";
import { useChromeHidden } from "@/shared/ui/compactChrome/useCompactChrome";

// Lazy so the chat bundle (markdown, charts, AG-UI client) loads on first open,
// not with the app shell. The surface hosts both the docked drawer and the
// full-screen dialog under one shared chat session.
const AgentAssistantSurface = lazy(() => import("./AgentAssistantSurface"));

function idleSpeakingEntry(): string | null {
  return null;
}

function initialVoiceActivity(): VoiceLaunchActivity {
  const supported = isVoiceInputSupported();
  return {
    listening: false,
    greeting: false,
    voiceMode: false,
    paused: false,
    phase: "idle",
    disabled: !supported,
    tooltip: describeVoiceState({ isSupported: supported, listening: false, error: null }),
  };
}

/**
 * Pointer handling for the launcher cluster. Compact chrome hides the idle
 * FAB on scroll and drops hits once the bars are off screen. A live voice
 * dialog keeps pointer events so pause/stop stay reachable.
 */
function clusterPointerEvents(compact: boolean): "auto" | undefined {
  if (compact) {
    return `var(${CHROME_FAB_POINTER_VAR}, auto)` as "auto";
  }
  return undefined;
}

function launcherBottom(compact: boolean, headerCompact: boolean): string {
  if (compact) {
    return chromeFabBottom(FAB_SCREEN_EDGE_PX);
  }
  if (headerCompact) {
    return `calc(var(--table-footer-clearance, 0px) + ${MOBILE_BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px) + ${FAB_SCREEN_EDGE_PX}px)`;
  }
  return desktopFabBottom();
}

/** Positioning and compact-chrome fade for the fixed launcher cluster. */
function launcherClusterSx(
  theme: Theme,
  opts: Readonly<{
    compact: boolean;
    dialogActive: boolean;
    headerCompact: boolean;
    open: boolean;
    stackPx: number;
  }>,
): SystemStyleObject<Theme> {
  const { compact, dialogActive, headerCompact, open, stackPx } = opts;
  return {
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    pt: dialogActive ? `${stackPx}px` : 0,
    pointerEvents: dialogActive ? "auto" : clusterPointerEvents(compact),
    right: `max(${FAB_SCREEN_EDGE_PX}px, env(safe-area-inset-right, 0px))`,
    bottom: launcherBottom(compact, headerCompact),
    // Idle FAB still fades with compact chrome. A live dialog must stay
    // tappable so pause/stop are not lost when the nav hides.
    opacity: compact && !dialogActive ? chromeFabOpacity() : undefined,
    willChange: compact && !dialogActive ? "bottom, opacity" : undefined,
    zIndex: open && dialogActive ? theme.zIndex.modal + 1 : theme.zIndex.drawer - 1,
  };
}

/** Whether two voice-activity snapshots are field-for-field equal. */
function voiceActivityEqual(a: VoiceLaunchActivity, b: VoiceLaunchActivity): boolean {
  return (
    a.listening === b.listening &&
    a.greeting === b.greeting &&
    a.voiceMode === b.voiceMode &&
    a.paused === b.paused &&
    a.phase === b.phase &&
    a.disabled === b.disabled &&
    a.tooltip === b.tooltip
  );
}

/** The FAB toggles pause/resume during a dialog, otherwise it opens the drawer. */
function handleFabClick(
  dialogActive: boolean,
  paused: boolean,
  issue: (action: VoiceLaunchAction) => void,
  openDrawer: () => void,
): void {
  if (dialogActive) {
    issue(paused ? "resume" : "pause");
    return;
  }
  openDrawer();
}

/**
 * Resolve a tap on the floating voice control into a launch command, depending
 * on whether a dialog is active, muted, listening, greeting, or still arming.
 */
function handleVoiceClick(
  ctx: Readonly<{
    activity: VoiceLaunchActivity;
    replySpeaking: boolean;
    pending: boolean;
    issue: (action: VoiceLaunchAction) => void;
    setCommand: Dispatch<SetStateAction<VoiceLaunchCommand | null>>;
    openForStart: () => void;
  }>,
): void {
  const { activity, replySpeaking, pending, issue, setCommand, openForStart } = ctx;
  if (activity.voiceMode) {
    issue("exit");
    return;
  }
  if (activity.disabled) {
    return;
  }
  if (activity.listening) {
    issue("send");
    return;
  }
  // Unlock the turn cue in this click. It plays later, after the assistant speaks.
  primeListenCue();
  if (activity.greeting || replySpeaking) {
    issue("interrupt");
    return;
  }
  if (pending) {
    setCommand((current) => {
      if (current?.action === "start") {
        return { id: current.id + 1, action: "start", skipGreeting: true };
      }
      return { id: (current?.id ?? 0) + 1, action: "interrupt" };
    });
    return;
  }
  openForStart();
  issue("start");
}

const PAUSED_LISTEN_ICON_PX = 20;

/** Same circle and shadow as pause/play, with the composer "Speak now" green. */
function pausedListenFabSx(theme: Theme): SystemStyleObject<Theme> {
  const assist = theme.app.aiAssist;
  const colors = voiceClusterPhaseColors(theme);
  const fill = colors.listening;
  const hover = voiceClusterPhaseHover(colors, "listening");
  const shadow = voiceClusterStatusShadow(fill, theme.palette.mode);
  return {
    width: TOWER_ASSISTANT_FAB_SIZE_PX,
    height: TOWER_ASSISTANT_FAB_SIZE_PX,
    minWidth: TOWER_ASSISTANT_FAB_SIZE_PX,
    minHeight: TOWER_ASSISTANT_FAB_SIZE_PX,
    p: 0,
    position: "relative",
    overflow: "visible",
    border: 0,
    borderRadius: "50%",
    bgcolor: fill,
    backgroundImage: "none",
    boxShadow: shadow,
    color: assist.onAccent,
    ...voiceClusterMotionSx(assist.gradientStart),
    "&:hover": {
      bgcolor: hover,
      backgroundImage: "none",
      boxShadow: shadow,
      color: assist.onAccent,
    },
    "&.Mui-disabled": {
      bgcolor: fill,
      backgroundImage: "none",
      color: assist.onAccent,
      boxShadow: shadow,
      opacity: 0.6,
    },
    ...voiceClusterReducedMotionSx(),
  };
}

function PausedListenButton({
  disabled,
  onClick,
}: Readonly<{ disabled: boolean; onClick: () => void }>) {
  const label = VOICE_PHASE_LABEL.listening;
  return (
    <Tooltip title={label}>
      <span>
        <Fab
          type="button"
          aria-label={label}
          aria-pressed={false}
          disabled={disabled}
          onClick={onClick}
          sx={pausedListenFabSx}
        >
          <MicRoundedIcon
            sx={{ position: "relative", zIndex: 1, fontSize: PAUSED_LISTEN_ICON_PX }}
          />
        </Fab>
      </span>
    </Tooltip>
  );
}

/**
 * The fixed, body-portaled launcher cluster: the idle assistant FAB, and while a
 * composer-started dialog runs, a stop button above and a show-conversation
 * button below. A paused dialog adds a speak-now mic between stop and play.
 * Split from the launcher so each keeps a small shape.
 */
function LauncherCluster({
  activity,
  compact,
  headerCompact,
  dialogActive,
  showFab,
  open,
  fabHidden,
  stackPx,
  unreadCount,
  onCloseClick,
  onListenClick,
  onFabClick,
  onAssistantClick,
}: Readonly<{
  activity: VoiceLaunchActivity;
  compact: boolean;
  headerCompact: boolean;
  dialogActive: boolean;
  showFab: boolean;
  open: boolean;
  fabHidden: boolean;
  stackPx: number;
  unreadCount: number;
  onCloseClick: () => void;
  onListenClick: () => void;
  onFabClick: () => void;
  onAssistantClick: () => void;
}>) {
  return createPortal(
    <Box
      inert={fabHidden && !dialogActive ? true : undefined}
      sx={(theme) =>
        launcherClusterSx(theme, { compact, dialogActive, headerCompact, open, stackPx })
      }
    >
      {dialogActive ? (
        <TowerAssistantVoiceButton
          revealed
          floating
          phase="idle"
          disabled={false}
          tooltip={VOICE_MODE_STOP_TOOLTIP}
          icon="close"
          label={VOICE_MODE_STOP_TOOLTIP}
          onClick={onCloseClick}
        />
      ) : null}
      {dialogActive && activity.paused ? (
        <Box sx={{ mb: `${VOICE_LAUNCH_GAP_PX}px`, lineHeight: 0 }}>
          <PausedListenButton disabled={activity.disabled} onClick={onListenClick} />
        </Box>
      ) : null}
      {showFab ? (
        <TowerAssistantFab
          unreadCount={unreadCount}
          dialog={dialogActive ? { phase: activity.phase, paused: activity.paused } : null}
          onClick={onFabClick}
        />
      ) : null}
      {dialogActive ? (
        <Box sx={{ mt: `${VOICE_LAUNCH_GAP_PX}px`, lineHeight: 0 }}>
          <TowerAssistantVoiceButton
            revealed
            floating={false}
            phase={activity.phase}
            disabled={false}
            tooltip="Show conversation"
            icon="assistant"
            label="Show conversation"
            onClick={onAssistantClick}
          />
        </Box>
      ) : null}
    </Box>,
    document.body,
  );
}

/**
 * App-wide entry point for the agent drawer (#673): a floating launcher on every
 * authenticated screen, gated by the TOWER_AGENT_ENABLED runtime flag. Renders
 * nothing at all when the feature is off. The badge counts scheduled-task
 * results delivered since the drawer was last opened (#677).
 */
export default function AgentDrawerLauncher({
  open: openProp,
  onOpenChange,
  width = AGENT_DRAWER_WIDTH,
  onWidthChange,
}: Readonly<{
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}> = {}) {
  const session = useOptionalAgentSession();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [everOpened, setEverOpened] = useState(false);
  const [pending, setPending] = useState(false);
  const [armed, setArmed] = useState(false);
  const [command, setCommand] = useState<VoiceLaunchCommand | null>(null);
  const [localActivity, setLocalActivity] = useState<VoiceLaunchActivity>(initialVoiceActivity);
  const activity = session?.activity ?? localActivity;
  const speakingEntryId = useSyncExternalStore(
    subscribeSpeech,
    getSpeakingEntryId,
    idleSpeakingEntry,
  );
  const tasks = useAgentTasks(agentConfig.enabled);
  const compact = useAiAssistCompactLayout();
  const headerCompact = useHeaderCompact();
  const fabHidden = useChromeHidden(compact);
  if (armed && !pending && !activity.voiceMode && !activity.greeting) {
    setArmed(false);
  }
  if (
    pending &&
    (activity.greeting || activity.listening || activity.voiceMode || activity.disabled)
  ) {
    setPending(false);
  }
  const replySpeaking = (armed || activity.voiceMode) && speakingEntryId !== null;
  const showFab = !open;
  const dialogActive = showFab && activity.voiceMode;
  // The idle launcher is only the assistant. A voice dialog started from the
  // composer adds close above it, turns the assistant into pause/play, and
  // puts show-conversation under that. Pause also stacks a speak-now mic
  // above play.
  const showCluster = showFab;

  if (!agentConfig.enabled) {
    return null;
  }

  const issue = (action: VoiceLaunchAction) => {
    if (session !== null) {
      session.issue(action);
      return;
    }
    setCommand((current) => ({
      id: (current?.id ?? 0) + 1,
      action,
    }));
  };

  const stackPx = VOICE_CLOSE_BUTTON_PX + VOICE_LAUNCH_GAP_PX;
  const openDrawer = () => {
    setEverOpened(true);
    setOpen(true);
  };
  const onVoiceClick = () => {
    handleVoiceClick({
      activity,
      replySpeaking,
      pending,
      issue,
      setCommand: (updater) => {
        if (session !== null) {
          session.issue("interrupt");
          return;
        }
        setCommand(updater);
      },
      openForStart: () => {
        setPending(true);
        setArmed(true);
        openDrawer();
      },
    });
  };
  const onVoiceActivity = (next: VoiceLaunchActivity) => {
    setLocalActivity((current) => (voiceActivityEqual(current, next) ? current : next));
  };

  return (
    <>
      {showCluster ? (
        <LauncherCluster
          activity={activity}
          compact={compact}
          headerCompact={headerCompact}
          dialogActive={dialogActive}
          showFab={showFab}
          open={open}
          fabHidden={fabHidden}
          stackPx={stackPx}
          unreadCount={tasks.unreadCount}
          onCloseClick={onVoiceClick}
          onListenClick={() => issue("listen")}
          onFabClick={() => handleFabClick(dialogActive, activity.paused, issue, openDrawer)}
          onAssistantClick={openDrawer}
        />
      ) : null}
      {open || everOpened ? (
        <Suspense fallback={null}>
          <AgentAssistantSurface
            open={open}
            onClose={() => setOpen(false)}
            tasks={tasks}
            width={width}
            onWidthChange={onWidthChange}
            voiceCommand={session !== null ? null : command}
            onVoiceActivity={session !== null ? undefined : onVoiceActivity}
            onRevealVisual={openDrawer}
          />
        </Suspense>
      ) : null}
    </>
  );
}
